import { useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.dev.aivilo.ai/api/v1';

interface ApiResult {
  success: boolean;
  error?: string;
}

interface MilestoneData {
  name: string;
  description?: string;
  start_date: string;
  end_date: string;
  status?: string;
}

export const useMilestones = () => {
  const [loading, setLoading] = useState(false);

  const createMilestone = async (projectId: string, milestoneData: MilestoneData): Promise<ApiResult> => {
    if (!projectId || !milestoneData.name || !milestoneData.start_date || !milestoneData.end_date) {
      return { success: false, error: 'Missing required fields' };
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/projects/${projectId}/milestones`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: milestoneData.name,
          description: milestoneData.description || '',
          start_date: milestoneData.start_date,
          end_date: milestoneData.end_date,
          status: milestoneData.status || 'PLANNED'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create milestone');
      }

      return { success: true };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error creating milestone:', err);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const updateMilestone = async (
    projectId: string,
    milestoneId: string,
    updates: Record<string, unknown>
  ): Promise<ApiResult> => {
    if (!projectId || !milestoneId) {
      return { success: false, error: 'Missing required fields' };
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/projects/${projectId}/milestones/${milestoneId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error('Failed to update milestone');
      }

      return { success: true };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error updating milestone:', err);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const deleteMilestone = async (projectId: string, milestoneId: string): Promise<ApiResult> => {
    if (!projectId || !milestoneId) {
      return { success: false, error: 'Missing required fields' };
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/projects/${projectId}/milestones/${milestoneId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete milestone');
      }

      return { success: true };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error deleting milestone:', err);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    createMilestone,
    updateMilestone,
    deleteMilestone
  };
};
